import { CalloutBox } from './CalloutBox';

/**
 * Custom components to be used in MDX files
 * These override the default markdown elements with styled versions
 */
export const MDXComponents = {
  // Custom components that can be used in MDX
  CalloutBox,
  
  // Override default markdown elements with inline styles to ensure proper formatting
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 
      style={{ 
        fontSize: '2.5rem', 
        fontWeight: 700, 
        color: '#1f2937', 
        marginTop: '3rem', 
        marginBottom: '1.5rem',
        lineHeight: 1.2
      }} 
      {...props} 
    />
  ),
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 
      style={{ 
        fontSize: '2rem', 
        fontWeight: 700, 
        color: '#1f2937', 
        marginTop: '3rem', 
        marginBottom: '1.5rem',
        lineHeight: 1.3,
        scrollMarginTop: '6rem'
      }} 
      {...props} 
    />
  ),
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 
      style={{ 
        fontSize: '1.5rem', 
        fontWeight: 600, 
        color: '#1f2937', 
        marginTop: '2.5rem', 
        marginBottom: '1rem',
        lineHeight: 1.4,
        scrollMarginTop: '6rem'
      }} 
      {...props} 
    />
  ),
  h4: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h4 
      style={{ 
        fontSize: '1.25rem', 
        fontWeight: 600, 
        color: '#1f2937', 
        marginTop: '2rem', 
        marginBottom: '0.75rem',
        lineHeight: 1.5
      }} 
      {...props} 
    />
  ),
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p 
      style={{ 
        fontSize: '1.125rem', 
        lineHeight: 1.75, 
        marginBottom: '1.5rem', 
        color: '#4B5563' 
      }} 
      {...props} 
    />
  ),
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
    <ul 
      style={{ 
        fontSize: '1.125rem', 
        lineHeight: 1.75, 
        marginTop: '1.5rem', 
        marginBottom: '1.5rem', 
        paddingLeft: '1.5rem', 
        color: '#4B5563',
        listStyleType: 'disc',
        listStylePosition: 'outside'
      }} 
      {...props} 
    />
  ),
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
    <ol 
      style={{ 
        fontSize: '1.125rem', 
        lineHeight: 1.75, 
        marginTop: '1.5rem', 
        marginBottom: '1.5rem', 
        paddingLeft: '1.5rem', 
        color: '#4B5563',
        listStyleType: 'decimal',
        listStylePosition: 'outside'
      }} 
      {...props} 
    />
  ),
  li: (props: React.HTMLAttributes<HTMLLIElement>) => (
    <li 
      style={{ 
        marginTop: '0.75rem', 
        marginBottom: '0.75rem', 
        paddingLeft: '0.5rem' 
      }} 
      {...props} 
    />
  ),
  blockquote: (props: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote 
      style={{ 
        borderLeft: '4px solid #34AFFB', 
        paddingLeft: '1.5rem', 
        paddingTop: '1rem', 
        paddingBottom: '1rem', 
        margin: '1.5rem 0', 
        fontStyle: 'italic', 
        color: '#4B5563', 
        backgroundColor: '#F4F7F9', 
        borderRadius: '0 0.5rem 0.5rem 0' 
      }} 
      {...props} 
    />
  ),
  code: (props: React.HTMLAttributes<HTMLElement>) => {
    if (!props.className) {
      return (
        <code 
          style={{ 
            backgroundColor: '#F4F7F9', 
            color: '#34AFFB', 
            padding: '0.125rem 0.5rem', 
            borderRadius: '0.25rem', 
            fontSize: '0.875rem',
            fontFamily: 'monospace'
          }} 
          {...props} 
        />
      );
    }
    return <code {...props} />;
  },
  pre: (props: React.HTMLAttributes<HTMLPreElement>) => (
    <pre 
      style={{ 
        backgroundColor: '#1f2937', 
        color: 'white', 
        padding: '1.5rem', 
        borderRadius: '0.5rem', 
        overflowX: 'auto', 
        margin: '1.5rem 0', 
        fontSize: '0.875rem' 
      }} 
      {...props} 
    />
  ),
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a 
      style={{ 
        color: '#34AFFB', 
        textDecoration: 'underline',
        transition: 'color 0.2s'
      }} 
      {...props} 
    />
  ),
  table: (props: React.HTMLAttributes<HTMLTableElement>) => (
    <div style={{ overflowX: 'auto', margin: '1.5rem 0' }}>
      <table 
        style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '0.875rem'
        }} 
        {...props} 
      />
    </div>
  ),
  thead: (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <thead style={{ backgroundColor: '#F4F7F9' }} {...props} />
  ),
  tbody: (props: React.HTMLAttributes<HTMLTableSectionElement>) => <tbody {...props} />,
  tr: (props: React.HTMLAttributes<HTMLTableRowElement>) => <tr {...props} />,
  th: (props: React.HTMLAttributes<HTMLTableCellElement>) => (
    <th 
      style={{ 
        padding: '0.75rem 1.5rem', 
        textAlign: 'left', 
        fontWeight: 600, 
        color: '#1f2937', 
        border: '1px solid rgba(75, 85, 99, 0.2)' 
      }} 
      {...props} 
    />
  ),
  td: (props: React.HTMLAttributes<HTMLTableCellElement>) => (
    <td 
      style={{ 
        padding: '0.75rem 1.5rem', 
        color: '#4B5563', 
        border: '1px solid rgba(75, 85, 99, 0.2)' 
      }} 
      {...props} 
    />
  ),
  hr: (props: React.HTMLAttributes<HTMLHRElement>) => (
    <hr 
      style={{ 
        margin: '2rem 0', 
        border: 0, 
        borderTop: '1px solid rgba(75, 85, 99, 0.2)' 
      }} 
      {...props} 
    />
  ),
  img: (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <img 
      style={{ 
        borderRadius: '0.5rem', 
        margin: '1.5rem 0', 
        width: '100%', 
        height: 'auto' 
      }} 
      {...props} 
      alt={props.alt || ''} 
    />
  ),
  strong: (props: React.HTMLAttributes<HTMLElement>) => (
    <strong style={{ fontWeight: 600, color: '#1f2937' }} {...props} />
  ),
  em: (props: React.HTMLAttributes<HTMLElement>) => (
    <em style={{ fontStyle: 'italic' }} {...props} />
  ),
};
